import PusherService from '../libs/pusher-service'
import { useRootStore } from '~/store/root'
import { useDealsStore } from '~/store/deals'
import { useBorrowerStore } from '~/store/borrower'
import { useDocumentsStore } from '~/store/documents'
import { useContractsStore } from '~/store/contracts'
import { useApprovalsStore } from '~/store/approvals'
import { useBorrowerUserActionStore } from '~/store/borrower-user-actions'
import { useOfferOptionsStore } from '~/store/offer-options'
import { useDocumentVerifications } from '~/composables/document-intelligence/useDocumentVerifications'
import { debounce } from 'es-toolkit/compat'

// Module-scoped (not a closure inside the exported middleware), so it persists across every
// route-middleware invocation for the life of the tab, the same way window.lendioPusher does.
// Compared against the CURRENT borrowerId on every navigation so a business switch - a same-tab
// SPA transition via store/business-switch.js's switchTo(), which never reconnects Pusher on its
// own - is detected here instead of being masked by the window.lendioPusher already-initialized
// guard (BR-16, EC-14).
let subscribedBorrowerId = null

export default defineNuxtRouteMiddleware(() => {
  // Skip server side
  if (typeof window === 'undefined') return

  // Skip if not logged in
  const rootStore = useRootStore()
  if (!rootStore.isLoggedIn) return

  const { borrowerId } = useBorrowerStore()

  // Already subscribed for this borrower - nothing to (re)initialize.
  if (window.lendioPusher && subscribedBorrowerId === borrowerId) return

  // A borrower switch leaves the tab bound to the outgoing borrower's private channel:
  // PusherService.connect() only derives the channel name from borrowerId at connect() time, so
  // the existing subscription must be torn down before reconnecting for the new one.
  //
  // Both flags are cleared here, before the reconnect attempt below, rather than left as-is until
  // a successful reconnect overwrites them. If the reconnect below fails (e.g. a transient missing
  // auth header), leaving them pointing at the outgoing borrower would satisfy the guard above the
  // next time the tab navigates back to that borrower - wrongly skipping a reconnect for a channel
  // that was, in fact, already torn down by this disconnect() call.
  if (window.lendioPusher) {
    PusherService.disconnect()
    window.lendioPusher = undefined
    subscribedBorrowerId = null
  }

  // Init Store Actions and Values
  const { getDealsWithOffers } = useDealsStore()
  const { injectDocument } = useDocumentsStore()
  const { getBorrower } = useBorrowerStore()
  const { getContracts, addContract } = useContractsStore()
  const { requestApprovals, getContractRequestsByDealId, getContractRequestsByBorrowerId } = useApprovalsStore()
  const { getActionItems } = useBorrowerUserActionStore()
  const { getOfferOptions } = useOfferOptionsStore()
  const { getDocuments } = useDocumentsStore()
  const { handleDocumentVerificationEvent } = useDocumentVerifications()

  const fetchBorrower = debounce(async () => await getBorrower({ forceReload: true }), 600)
  const fetchApprovals = debounce(() => requestApprovals(true), 600)
  const fetchOfferOptions = debounce(() => getOfferOptions(true), 600)
  const fetchContracts = debounce(() => getContracts(), 600)
  const fetchContractRequests = debounce((dealId) => getContractRequestsByDealId(dealId), 600)
  const fetchContractRequestsByBorrower = debounce(() => getContractRequestsByBorrowerId(borrowerId), 600)
  const fetchUserActions = debounce(() => getActionItems(true), 600)
  const fetchNeededDocuments = debounce(() =>  getDocuments(true), 600)

  const fetchFinancingOptions = debounce(async () => {
    getDealsWithOffers()
    requestApprovals(true)
    getOfferOptions(true)
  }, 1500)

  const documentHandler = (data) => {
    if (data?.object && data.object.borrowerId === borrowerId && !data.object.deleted) {
      injectDocument(data.object)
    }
  }

  const handleContractEvent = (type, data) => {
    switch (type) {
      case 'create':
        addContract(data.contract)
        break;
      case 'delete':
        fetchApprovals()
        fetchContractRequests(data.contract.dealId)
        break;
    }
    fetchContracts()
  }

  const fetchContractRequestsForAcceptedDeal = (deal) => {
    if (!deal) {
      return;
    }

    if (['offerAccepted', 'contractRequested', 'contractOut', 'contractIn', 'funding'].includes(deal?.status)) {
      fetchContractRequestsByBorrower()
    }
  }

  PusherService.connect()

  const handleModelChange = async (type, data) => {
    switch (data.resourceName) {
      case 'deal':
        fetchFinancingOptions()
        fetchContractRequestsForAcceptedDeal(data.object)
        break;
      case 'borrower':
        fetchBorrower()
        break;
      case 'document':
        documentHandler(data)
        break;
      case 'approval':
        fetchApprovals()
        break;
      case 'contract':
        handleContractEvent(type, data)
        break;
      case 'userActions':
        fetchNeededDocuments()
        fetchUserActions()
        documentHandler(data)
        break;
      case 'offerOption':
        fetchOfferOptions()
        break;
      case 'recommendedFinancingOption':
        fetchFinancingOptions()
        break;
      case 'document-verification':
        handleDocumentVerificationEvent(type, data, borrowerId)
        break;
    }
  }

  const BorrowerChannel = PusherService.getBorrowerChannel()

  // Only commit "subscribed" state when connect() actually produced a channel. connect() can
  // return false (e.g. a transient missing auth header) right after the disconnect() above -
  // committing anyway would wrongly mark this borrowerId as done and, since window.lendioPusher
  // would stay set from before, permanently short-circuit every later navigation's retry via the
  // guard above. Leaving both unset here means the very next navigation retries the whole
  // connect/subscribe attempt again.
  if (BorrowerChannel) {
    // Borrower model update subscriptions
    BorrowerChannel.bind_global(handleModelChange)
    window.lendioPusher = PusherService
    subscribedBorrowerId = borrowerId
  }
})
